import React, { useMemo, useState } from 'react'
import {
  ChartFrame,
  SMOOTH_AREA_CHART_DEFAULT_HEIGHT,
  SMOOTH_AREA_CHART_DEFAULT_WIDTH,
  SMOOTH_AREA_CHART_STROKE_COLOR,
  SmoothAreaChart,
  type AreaChartPoint,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './SmoothAreaChartShowcase.css'

const SAMPLE_COLLECTION: AreaChartPoint[] = [
  { label: 'Mon', value: 48 },
  { label: 'Tue', value: 62 },
  { label: 'Wed', value: 55 },
  { label: 'Thu', value: 78 },
  { label: 'Fri', value: 91 },
  { label: 'Sat', value: 70 },
  { label: 'Sun', value: 58 },
]

const SmoothAreaChartShowcase: React.FC = () => {
  const [showXAxisLabel, setShowXAxisLabel] = useState(false)
  const [showYAxisLabel, setShowYAxisLabel] = useState(false)
  const [width, setWidth] = useState(SMOOTH_AREA_CHART_DEFAULT_WIDTH)
  const [height, setHeight] = useState(SMOOTH_AREA_CHART_DEFAULT_HEIGHT)

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'smooth-area-chart-width',
            label: `Width (${width}px)`,
            control: (
              <input
                id="smooth-area-chart-width"
                className="ui-field-control"
                type="range"
                min={280}
                max={800}
                step={8}
                value={width}
                onChange={(event) => setWidth(Number(event.target.value))}
              />
            ),
          },
          {
            id: 'smooth-area-chart-height',
            label: `Height (${height}px)`,
            control: (
              <input
                id="smooth-area-chart-height"
                className="ui-field-control"
                type="range"
                min={120}
                max={360}
                step={8}
                value={height}
                onChange={(event) => setHeight(Number(event.target.value))}
              />
            ),
          },
          {
            id: 'smooth-area-chart-x-title',
            label: 'X axis title',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="smooth-area-chart-x-title"
                  type="checkbox"
                  checked={showXAxisLabel}
                  onChange={(event) => setShowXAxisLabel(event.target.checked)}
                />
                <span>Show “Day”</span>
              </label>
            ),
          },
          {
            id: 'smooth-area-chart-y-title',
            label: 'Y axis title',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="smooth-area-chart-y-title"
                  type="checkbox"
                  checked={showYAxisLabel}
                  onChange={(event) => setShowYAxisLabel(event.target.checked)}
                />
                <span>Show</span>
              </label>
            ),
          },
        ],
      },
    ],
    [height, showXAxisLabel, showYAxisLabel, width],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Smooth area chart"
        description="Curved area series with blue-500 stroke and fill — framed with sample axes and legend."
        stageClassName="ui-preview__stage--wide smooth-area-chart-showcase__stage-wrap"
      >
        <div className={`smooth-area-chart-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Sample weekly collection · {width}×{height}px
            </p>
            <div
              className="smooth-area-chart-showcase__live-frame"
              style={{ width, maxWidth: '100%' }}
            >
              <ChartFrame
                plotHeight={height}
                yTicks={['100', '75', '50', '25', '0']}
                yAxisLabel="Collection %"
                showYAxisLabel={showYAxisLabel}
                xLabels={SAMPLE_COLLECTION.map((point) => point.label ?? '')}
                xAxisLabel="Day"
                showXAxisLabel={showXAxisLabel}
                legend={[
                  {
                    label: 'Collection',
                    color: SMOOTH_AREA_CHART_STROKE_COLOR,
                  },
                ]}
              >
                <SmoothAreaChart
                  data={SAMPLE_COLLECTION}
                  width={width}
                  height={height}
                  maxValue={100}
                  seriesName="Collection"
                  aria-label="Sample weekly collection"
                />
              </ChartFrame>
            </div>
          </section>

          <section className="smooth-area-chart-showcase__matrix">
            <h4 className="smooth-area-chart-showcase__section-title">
              Default
            </h4>
            <div
              className={`smooth-area-chart-showcase__row smooth-area-chart-showcase__row--selected`}
            >
              <span className="smooth-area-chart-showcase__row-label">
                Figma default
              </span>
              <div className="smooth-area-chart-showcase__row-preview">
                <SmoothAreaChart />
              </div>
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Smooth area chart"
        sections={propertySections}
      />
    </>
  )
}

export default SmoothAreaChartShowcase
